import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pencil, PackagePlus, Settings2, X, Loader2, Trash2, Plus, Search } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { STATE_META, fmtDate } from "./licenseUtil";

const INPUT = "w-full px-3 py-2 text-sm rounded-lg border border-[color:var(--bd-sub)] bg-white focus:border-indigo-500 outline-none";
const ATTENTION = { blocked: 0, trial_ended: 0, grace: 1, warning: 2 };
const num = (v) => (v === "" || v == null ? null : Number(v));

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block text-sm"><span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>{children}</label>;
}

function SaveBtn({ busy, testid, children = "Save" }) {
  return (
    <button type="submit" disabled={busy} data-testid={testid} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50">
      {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
    </button>
  );
}

function EditLicense({ t, plans, onClose, onSaved }) {
  const l = t.license || {};
  const [f, setF] = useState({ plan: l.plan || "enterprise", custom_allowance: l.custom_allowance ?? "", custom_overage_price: l.custom_overage_price ?? "",
    grace_pct: l.grace_pct ?? 10, trial_ends_at: (l.trial_ends_at || "").slice(0, 10), notes: l.notes || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/admin/tenants/${t.tenant_id}/license`, { ...f, custom_allowance: num(f.custom_allowance), custom_overage_price: num(f.custom_overage_price),
        grace_pct: Number(f.grace_pct) || 0, trial_ends_at: f.plan === "trial" && f.trial_ends_at ? new Date(`${f.trial_ends_at}T23:59:59Z`).toISOString() : null });
      toast.success("Licence updated"); onSaved(); onClose();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };
  const p = plans.find((x) => x.key === f.plan);
  return (
    <Modal title={`Licence · ${t.tenant_name}`} onClose={onClose} testid="license-edit-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Plan">
          <select className={INPUT} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="license-edit-plan">
            {plans.map((x) => <option key={x.key} value={x.key}>{x.name}{x.monthly_interviews == null ? " (unlimited)" : ` · ${x.monthly_interviews}/mo`}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Interviews / mo (plan: ${p?.monthly_interviews ?? "∞"})`}>
            <input type="number" min="0" className={INPUT} value={f.custom_allowance} placeholder="Use plan" onChange={(e) => setF({ ...f, custom_allowance: e.target.value })} data-testid="license-edit-allowance" />
          </Field>
          <Field label={`Extra price $ (plan: ${p?.overage_price ?? "—"})`}>
            <input type="number" min="0" step="0.5" className={INPUT} value={f.custom_overage_price} placeholder="Use plan" onChange={(e) => setF({ ...f, custom_overage_price: e.target.value })} data-testid="license-edit-overage" />
          </Field>
          <Field label="Grace %">
            <input type="number" min="0" max="100" className={INPUT} value={f.grace_pct} onChange={(e) => setF({ ...f, grace_pct: e.target.value })} data-testid="license-edit-grace" />
          </Field>
          {f.plan === "trial" && (
            <Field label="Trial ends">
              <input type="date" className={INPUT} value={f.trial_ends_at} onChange={(e) => setF({ ...f, trial_ends_at: e.target.value })} data-testid="license-edit-trial-end" />
            </Field>
          )}
        </div>
        <Field label="Internal notes (e.g. contract reference)">
          <input className={INPUT} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="license-edit-notes" />
        </Field>
        <div className="flex justify-end"><SaveBtn busy={busy} testid="license-edit-save" /></div>
      </form>
    </Modal>
  );
}

function AddCredits({ t, packs, onClose, onSaved }) {
  const [f, setF] = useState({ pack: packs[0]?.key || "custom", interviews: "", validity_days: 365, note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/admin/tenants/${t.tenant_id}/credits`, f.pack === "custom"
        ? { interviews: Number(f.interviews), validity_days: Number(f.validity_days) || 365, note: f.note } : { pack: f.pack, note: f.note });
      toast.success("Credits added"); onSaved(); onClose();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Modal title={`Add credits · ${t.tenant_name}`} onClose={onClose} testid="license-credits-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Pack">
          <select className={INPUT} value={f.pack} onChange={(e) => setF({ ...f, pack: e.target.value })} data-testid="license-credits-pack">
            {packs.map((p) => <option key={p.key} value={p.key}>{p.name}{p.price != null ? ` · $${p.price}` : ""} · {p.validity_days} days</option>)}
            <option value="custom">Custom amount</option>
          </select>
        </Field>
        {f.pack === "custom" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Interviews"><input type="number" min="1" required className={INPUT} value={f.interviews} onChange={(e) => setF({ ...f, interviews: e.target.value })} data-testid="license-credits-amount" /></Field>
            <Field label="Valid for (days)"><input type="number" min="1" className={INPUT} value={f.validity_days} onChange={(e) => setF({ ...f, validity_days: e.target.value })} data-testid="license-credits-days" /></Field>
          </div>
        )}
        <Field label="Note (e.g. invoice number)"><input className={INPUT} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="license-credits-note" /></Field>
        {(t.credits || []).length > 0 && (
          <div className="text-sm">
            <div className="text-xs font-medium text-slate-600 mb-1">Current credits</div>
            {t.credits.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-1">
                <span className="flex-1 truncate">{c.name} · {c.remaining}/{c.interviews} · exp {fmtDate(c.expires_at)}</span>
                <button type="button" data-testid={`license-credit-remove-${c.id}`} title="Remove" className="text-slate-400 hover:text-rose-600"
                  onClick={async () => {
                    if (!window.confirm("Remove this credit pack?")) return;
                    try { await api.delete(`/admin/tenants/${t.tenant_id}/credits/${c.id}`); toast.success("Credit pack removed"); onSaved(); onClose(); }
                    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
                  }}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end"><SaveBtn busy={busy} testid="license-credits-save">Add credits</SaveBtn></div>
      </form>
    </Modal>
  );
}

function Catalog({ plans, packs, onClose, onSaved }) {
  const [pl, setPl] = useState(plans.map((p) => ({ ...p })));
  const [pk, setPk] = useState(packs.map((p) => ({ ...p })));
  const [busy, setBusy] = useState(false);
  const setP = (i, k, v) => setPl(pl.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const setK = (i, k, v) => setPk(pk.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/admin/license-catalog", { plans: pl, packs: pk }); toast.success("Plans & packs saved"); onSaved(); onClose(); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    setBusy(false);
  };
  const cell = "px-2 py-1.5 text-sm rounded-md border border-[color:var(--bd-sub)] w-full min-w-0";
  return (
    <Modal title="Plans & credit packs" onClose={onClose} testid="license-catalog-dialog" wide>
      <form onSubmit={submit} className="space-y-6">
        <div className="overflow-x-auto">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Plans (leave interviews blank for unlimited)</div>
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-[11px] text-slate-500 text-left"><th className="pb-1">Name</th><th>Interviews / mo</th><th>Extra $</th><th>$ / mo</th><th>$ / mo annual</th></tr></thead>
            <tbody>{pl.map((p, i) => (
              <tr key={p.key} data-testid={`catalog-plan-${p.key}`}>
                {["name", "monthly_interviews", "overage_price", "price_monthly", "price_annual"].map((k) => (
                  <td key={k} className="pr-2 py-1"><input className={cell} value={p[k] ?? ""} type={k === "name" ? "text" : "number"} min="0" onChange={(e) => setP(i, k, e.target.value)} data-testid={`catalog-plan-${p.key}-${k}`} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="overflow-x-auto">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Credit packs</div>
          <table className="w-full text-sm min-w-[520px]">
            <thead><tr className="text-[11px] text-slate-500 text-left"><th className="pb-1">Name</th><th>Interviews</th><th>Price $</th><th>Valid days</th><th /></tr></thead>
            <tbody>{pk.map((p, i) => (
              <tr key={p.key || i} data-testid={`catalog-pack-${i}`}>
                {["name", "interviews", "price", "validity_days"].map((k) => (
                  <td key={k} className="pr-2 py-1"><input className={cell} value={p[k] ?? ""} type={k === "name" ? "text" : "number"} min="0" onChange={(e) => setK(i, k, e.target.value)} data-testid={`catalog-pack-${i}-${k}`} /></td>
                ))}
                <td><button type="button" onClick={() => setPk(pk.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`catalog-pack-${i}-remove`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}</tbody>
          </table>
          <button type="button" onClick={() => setPk([...pk, { name: "", interviews: "", price: "", validity_days: 365 }])} data-testid="catalog-pack-add"
            className="mt-2 inline-flex items-center gap-1 text-sm text-indigo-600"><Plus className="w-4 h-4" /> Add pack</button>
        </div>
        <p className="text-xs text-slate-500">The public Pricing page isn't changed automatically.</p>
        <div className="flex justify-end"><SaveBtn busy={busy} testid="catalog-save" /></div>
      </form>
    </Modal>
  );
}

export default function LicensesPanel({ data, reload }) {
  const [dlg, setDlg] = useState(null);
  const [q, setQ] = useState("");
  if (!data) return null;
  const { plans, packs } = data;
  const needle = q.trim().toLowerCase();
  const tenants = data.tenants
    .filter((t) => !needle || (t.tenant_name || "").toLowerCase().includes(needle))
    .sort((a, b) => (ATTENTION[a.state] ?? 9) - (ATTENTION[b.state] ?? 9) || (b.pct || 0) - (a.pct || 0));
  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid="licenses-panel">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
        <KeyRound className="w-4 h-4 text-indigo-500" />
        <h2 className="font-display font-semibold">All workspaces</h2>
        <span className="text-xs text-slate-500">{data.tenants.filter((t) => t.state in ATTENTION).length} need attention</span>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workspaces" data-testid="licenses-search"
            className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 focus:border-indigo-500 outline-none w-52" />
        </div>
        <button type="button" onClick={() => setDlg({ kind: "catalog" })} data-testid="licenses-catalog-btn"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50"><Settings2 className="w-4 h-4" /> Plans &amp; packs</button>
      </div>
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr><th className="text-left px-4 py-2.5">Workspace</th><th className="text-left px-3">Plan</th><th className="text-left px-3">This month</th><th className="text-right px-3">Credits</th><th className="text-right px-3">Extra</th><th className="text-left px-3">Status</th><th className="px-3" /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!tenants.length && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-500" data-testid="licenses-empty">No workspaces match.</td></tr>}
            {tenants.map((t) => {
              const meta = STATE_META[t.state] || STATE_META.ok;
              return (
                <tr key={t.tenant_id} data-testid={`license-row-${t.tenant_id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-900 max-w-[220px] truncate">{t.tenant_name}</td>
                  <td className="px-3 whitespace-nowrap">{t.plan.name}{t.license?.custom_allowance != null ? " · custom" : ""}</td>
                  <td className="px-3 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums whitespace-nowrap" data-testid={`license-row-used-${t.tenant_id}`}>{t.used}{t.unlimited ? "" : ` / ${t.allowance}`}</span>
                      {!t.unlimited && <div className="flex-1 h-1.5 rounded-full bg-slate-100 min-w-[60px]"><div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${Math.min(100, t.pct || 0)}%` }} /></div>}
                    </div>
                  </td>
                  <td className="px-3 text-right tabular-nums">{t.credits_left}</td>
                  <td className="px-3 text-right tabular-nums">{t.overage}{t.billable_overage ? ` · $${t.billable_overage}` : ""}</td>
                  <td className="px-3"><span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${meta.cls}`}>{meta.label}</span></td>
                  <td className="px-3 whitespace-nowrap text-right">
                    <button type="button" title="Edit licence" onClick={() => setDlg({ kind: "edit", t })} data-testid={`license-edit-${t.tenant_id}`} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"><Pencil className="w-4 h-4" /></button>
                    <button type="button" title="Add credits" onClick={() => setDlg({ kind: "credits", t })} data-testid={`license-credits-${t.tenant_id}`} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"><PackagePlus className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dlg?.kind === "edit" && <EditLicense t={dlg.t} plans={plans} onClose={() => setDlg(null)} onSaved={reload} />}
      {dlg?.kind === "credits" && <AddCredits t={dlg.t} packs={packs} onClose={() => setDlg(null)} onSaved={reload} />}
      {dlg?.kind === "catalog" && <Catalog plans={plans} packs={packs} onClose={() => setDlg(null)} onSaved={reload} />}
    </section>
  );
}
